import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  GetFollowUpDetailsByTenateId,
  FilterSiteVisitAsync,
  GetSiteVisitedDataByCount,
} from "../Services/APIService";

interface UserSiteVisitedSummary {
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  loginUserName: string | null;
  tenantId: number;
  completedTaskCount: number;
  leadIds: number[];
  taskIds: number[];
}

interface FilterParams {
  tenantId: number;
  fromDate?: string;
  toDate?: string;
  userId?: number;
  sourceId?: number;
  subSourceId?: number;
  leadTypeId?: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface User {
  userId: number;
  userName: string;
  userTypeId: number;
  isActive: boolean;
  reportTo: number;
}

interface OptionType {
  value: number;
  label: string;
}

const SiteVisitedReport: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSummaries, setUserSummaries] = useState<UserSiteVisitedSummary[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isCustomerExporting, setIsCustomerExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>("");

  // Dropdown data
  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredSubSources, setFilteredSubSources] = useState<SubMediaSource[]>([]);

  // Filter state
  const [filterParams, setFilterParams] = useState<FilterParams>({
    tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
    fromDate: "",
    toDate: "",
    userId: undefined,
    sourceId: undefined,
    subSourceId: undefined,
    leadTypeId: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getMenus();
  }, [location.state]);

  const fetchDropdownData = async () => {
    try {
      const sourceResponse = await fetchMeadiaSourceData();
      setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

      const subSourceResponse = await fetchAllSubMediaSource();
      setSubMediaSources(subSourceResponse.filter((s: SubMediaSource) => s.isActive));

      const userResponse = await fetchUserData();
      setUsers(
        userResponse.filter(
          (u: User) => (u.userTypeId === 2 || u.userTypeId === 4) && u.isActive
        )
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load filter options");
    }
  };

  const fetchSiteVisitedDetails = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "";
      const response = await GetFollowUpDetailsByTenateId(tenantId);
      setUserSummaries(
        (response || []).map((item: any) => ({
          ...item,
          leadIds: item.leadIds || [],
          taskIds: item.taskIds || [],
        }))
      );
    } catch (error) {
      console.error("Error fetching site visited details:", error);
      toast.error("Failed to load site visited details");
    } finally {
      setLoading(false);
    }
  };

  // const formatDate = (date: Date): string => {
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const day = String(date.getDate()).padStart(2, "0");

  //   return `${year}-${month}-${day}`;
  // };
  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${year}-${month}-${day}`; // for input type="date"
  };
  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);

    const today = new Date();

    let fromDate = "";
    let toDate = "";

    switch (period) {
      case "today":
        fromDate = formatDate(today);
        toDate = formatDate(today);
        break;

      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        fromDate = formatDate(yesterday);
        toDate = formatDate(yesterday);
        break;
      }

      case "thisMonth": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today);
        break;
      }

      case "lastMonth": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );

        const lastDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(lastDay);
        break;
      }

      case "last3Months": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today); // include current date
        break;
      }

      case "last6Months": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 6,
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today); // include current date
        break;
      }

      default:
        setFilterParams((prev) => ({
          ...prev,
          fromDate: "",
          toDate: "",
        }));
        return;
    }

    setFilterParams((prev) => ({
      ...prev,
      fromDate,
      toDate,
    }));
  };
  const applyFilter = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "1000";
      const apiFilterParams: any = { tenantId };
      if (filterParams.fromDate) apiFilterParams.fromDate = filterParams.fromDate;
      if (filterParams.toDate) apiFilterParams.toDate = filterParams.toDate;
      if (filterParams.userId) apiFilterParams.userId = filterParams.userId;

      const data = await FilterSiteVisitAsync(apiFilterParams);
      setUserSummaries(
        (data || []).map((item: any) => ({
          ...item,
          leadIds: item.leadIds || [],
          taskIds: item.taskIds || [],
        }))
      );
      toast.success("Filter applied successfully");
    } catch (error) {
      console.error("Error applying filter:", error);
      toast.error("Failed to apply filter");
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setFilterParams({
      tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
      fromDate: "",
      toDate: "",
      userId: undefined,
      sourceId: undefined,
      subSourceId: undefined,
      leadTypeId: undefined,
    });
    setSelectedPeriod("");
    setFilteredSubSources([]);
    fetchSiteVisitedDetails();
  };

  const handleCompletedTaskClick = (
    userId: number,
    userName: string,
    leadIds: number[],
    taskIds: number[]
  ) => {
    const tenantId = localStorage.getItem("tenantId") || "";
    const url = `/#site-visited-details?userId=${userId}&tenantId=${tenantId}&userName=${encodeURIComponent(userName)}&leadIds=${leadIds.join(",")}&taskIds=${taskIds.join(",")}`;
    window.open(url, "_blank");
  };

  const exportToExcel = () => {
    try {
      const headers = ["User Name", "Mobile", "Email", "Completed Tasks"];
      const rows = filteredUsers.map((user) => [
        user.userName,
        user.mobile,
        user.email,
        user.completedTaskCount || 0,
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `site-visited-report-${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToPDF = () => {
    try {
      const printContent = `
        <!DOCTYPE html><html>
        <head><title>Site Visited Report</title>
        <style>
          @page { size: A4 landscape; margin: 1cm; }
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #333; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #a78590; color: white; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .count-cell { text-align: center; font-weight: bold; }
          .meta-info { margin-bottom: 15px; font-size: 12px; color: #666; }
        </style></head>
        <body>
          <h1>Site Visited Report</h1>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            ${filterParams.fromDate ? `<strong>From Date:</strong> ${filterParams.fromDate}<br/>` : ""}
            ${filterParams.toDate ? `<strong>To Date:</strong> ${filterParams.toDate}<br/>` : ""}
            <strong>Total Records:</strong> ${filteredUsers.length}
          </div>
          <table>
            <thead><tr><th>User Name</th><th>Mobile</th><th>Email</th><th>Completed Tasks</th></tr></thead>
            <tbody>
              ${filteredUsers
          .map(
            (user) => `
                <tr>
                  <td>${user.userName}</td>
                  <td>${user.mobile}</td>
                  <td>${user.email}</td>
                  <td class="count-cell">${user.completedTaskCount || 0}</td>
                </tr>`
          )
          .join("")}
            </tbody>
          </table>
        </body></html>`;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  // ============================================================
  // ✅ CUSTOMER EXPORT
  // Uses GetSiteVisitedDataByCount from APIService (includes auth
  // token + BASE_URL) for EACH user. Exports every detail row
  // across all users into a single CSV/Excel with:
  //   - Columns: Lead Name, Project, Follow-up Type,
  //     Follow-up Date, Task Notes, Completion Date, Status
  //   - Subtotal row after each user  (e.g. Admin — Total Visits: 59)
  //   - Grand Total row at the bottom (e.g. Total Visits: 69)
  // ============================================================

  const exportCustomerExcel = async () => {
    try {
      setIsCustomerExporting(true);
      setExportProgress("Preparing export...");

      const tenantId = parseInt(localStorage.getItem("tenantId") || "1000");

      // Only users that actually have leadIds or taskIds
      const usersToExport = filteredUsers.filter(
        (u) =>
          (u.leadIds && u.leadIds.length > 0) ||
          (u.taskIds && u.taskIds.length > 0)
      );

      if (usersToExport.length === 0) {
        toast.error("No users with lead/task data to export");
        return;
      }

      // ---- Fetch via APIService (handles Bearer token + BASE_URL internally) ----
      const allResults: { user: UserSiteVisitedSummary; data: any[] }[] = [];

      for (let i = 0; i < usersToExport.length; i++) {
        const user = usersToExport[i];
        setExportProgress(
          `Fetching data for "${user.userName}" (${i + 1} of ${usersToExport.length})...`
        );

        try {
          // Calls: GET BASE_URL/ReportApi/GetSiteVisitedDataByCount
          //        ?userid=X&tenantId=Y&leadId=1&leadId=2&taskId=1...
          // Returns: data.tasks || data.data || data  (handled inside APIService)
          const data = await GetSiteVisitedDataByCount({
            userId: user.userId,
            tenantId: tenantId,
            leadIds: user.leadIds || [],
            taskIds: user.taskIds || [],
          });

          allResults.push({
            user,
            data: Array.isArray(data) ? data : [],
          });
        } catch (err) {
          console.error(`Failed to fetch for ${user.userName}:`, err);
          allResults.push({ user, data: [] }); // still include user, just 0 rows
        }
      }

      setExportProgress("Building Excel file...");

      // ---- CSV columns (matching the detail page: Lead Name, Project, Follow-up Type,
      //      Follow-up Date, Task Notes, Completion Date, Status) ----
      const headers = [
        "Sr No",
        "Lead Id",
        "Lead Name",
        "Mobile Number",
        "Lead Date",
        "Site Visited Date",
        "Site Visited By",
        "Project",
        "Follow-up Type",
        "Remark",
        "Last Follow-up Date",
        "Last Updated By",
        "Status",
      ];

      // CSV cell escaper
      const esc = (val: any): string =>
        `"${String(val ?? "").replace(/"/g, '""')}"`;

      let csvContent = headers.map(esc).join(",") + "\n";

      let grandTotal = 0;
      let srNo = 1;

      allResults.forEach(({ user, data }) => {
        if (data.length === 0) return; // skip users with no rows

        // --- Detail rows for this user ---
        data.forEach((row: any) => {
          csvContent +=
            [
              srNo++,

              // Lead Id
              row.leadId ?? "",

              // Lead Name
              row.leadName ?? row.customerName ?? row.name ?? "",

              // Mobile Number
              row.leadMobileNo ?? row.mobileNo ?? row.phoneNumber ?? "",

              // Lead Date
              formatDate(row.createdOn),

              // Site Visited Date
              formatDate(row.followupDate),

              // Site Visited By
              row.assignedUserName ?? user.userName ?? "",

              // Project
              row.projectName ?? "",

              // Follow-up Type
              row.followupTypeName ??
              row.followUpType ??
              row.followupType ??
              "Site Visit",

              // Remark
              row.taskNotes ?? row.notes ?? row.remark ?? "",

              // Last Follow-up Date
              row.modifiedOn
                ? formatDate(row.modifiedOn)
                : formatDate(row.followupDate),

              // Last Updated By
              row.modifiedByName ??
              row.createdByName ??
              "",

              // Status
              row.isCompleted
                ? "Completed"
                : row.isCanceled
                  ? "Canceled"
                  : row.isPostponed
                    ? "Postponed"
                    : "Pending",
            ]
              .map(esc)
              .join(",") + "\n";
        });

        const userCount = data.length;
        grandTotal += userCount;

        // --- Subtotal row for this user ---
        csvContent +=
          ["", `${user.userName} — Total Visits: ${userCount}`, "", "", "", "", "", "", ""]
            .map(esc)
            .join(",") + "\n";

        // Blank separator between users
        csvContent += Array(headers.length).fill(`""`).join(",") + "\n";
      });

      // --- Grand total row ---
      csvContent +=
        [`Total Visits: ${grandTotal}`]
          .map(esc)
          .join(",") + "\n";

      // ---- Trigger download ----
      // \uFEFF = UTF-8 BOM so Excel opens special chars correctly
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `customer-site-visit-export-${new Date().toISOString().split("T")[0]
        }.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const exportedUsers = allResults.filter((r) => r.data.length > 0).length;
      toast.success(
        `Customer export done! ${grandTotal} total rows across ${exportedUsers} user(s).`
      );
    } catch (error) {
      console.error("Customer export error:", error);
      toast.error("Failed to export customer data");
    } finally {
      setIsCustomerExporting(false);
      setExportProgress("");
    }
  };

  useEffect(() => {
    fetchSiteVisitedDetails();
    fetchDropdownData();
  }, []);

  const handleSourceOnChange = (sourceId: number | undefined) => {
    setFilterParams({ ...filterParams, sourceId, subSourceId: undefined });
  };

  useEffect(() => {
    if (filterParams.sourceId && subMediaSources.length > 0) {
      const filtered = subMediaSources.filter(
        (src) => src.mediaSourceID === filterParams.sourceId
      );
      setFilteredSubSources(filtered);
      if (!filtered.some((s) => s.id === filterParams.subSourceId)) {
        setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
      }
    } else {
      setFilteredSubSources([]);
      setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
    }
  }, [filterParams.sourceId, subMediaSources]);

  const filteredUsers = userSummaries.filter(
    (user) =>
      user.userName.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.mobile.includes(searchText)
  );

  const getSelectedOption = (options: OptionType[], value?: number) =>
    options.find((option) => option.value === value) || null;

  const userTypeId = Number(localStorage.getItem("userTypeId"));
  const loggedUserId = Number(localStorage.getItem("UserId"));
  const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

  let filteredUserList = users;
  if (userTypeId === 1) {
    filteredUserList = users;
  } else if (userTypeId === 2) {
    filteredUserList = isLoginAsManager
      ? users.filter((u) => u.userId === loggedUserId || u.reportTo === loggedUserId)
      : users.filter((u) => u.userId === loggedUserId);
  } else {
    filteredUserList = users.filter((u) => u.userId === loggedUserId);
  }

  const userOptions: OptionType[] = filteredUserList.map((u) => ({
    value: u.userId,
    label: u.userName,
  }));

  const columns = [
    {
      name: "User Name",
      selector: (row: UserSiteVisitedSummary) => row.userName,
      sortable: true,
      width: "250px",
      cell: (row: UserSiteVisitedSummary) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>{row.userName}</span>
        </div>
      ),
    },
    {
      name: "Email",
      selector: (row: UserSiteVisitedSummary) => row.email,
      sortable: true,
      width: "250px",
    },
    {
      name: "Visited Lead Count",
      cell: (row: UserSiteVisitedSummary) => (
        <span
          className="status-count-badge completed-badge"
          onClick={() =>
            handleCompletedTaskClick(
              row.userId,
              row.userName,
              row.leadIds || [],
              row.taskIds || []
            )
          }
          style={{ cursor: "pointer" }}
        >
          {row.completedTaskCount || 0}
        </span>
      ),
      width: "150px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Site Visited Report</div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search by Name, Email or Mobile"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />

                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>

                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>

                        {/* ✅ Customer Export — exports ALL detail rows from ALL users */}
                        <button
                          className="demo-btn btn btn-outline-warning btn-sm"
                          onClick={exportCustomerExcel}
                          disabled={isCustomerExporting || filteredUsers.length === 0}
                          title="Export detailed customer visit data for all users"
                          style={{ minWidth: "155px" }}
                        >
                          {isCustomerExporting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                              />
                              Exporting...
                            </>
                          ) : (
                            <>
                              <i
                                className="bi bi-people-fill"
                                style={{ marginRight: "4px" }}
                              ></i>
                              Customer Export
                            </>
                          )}
                        </button>

                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                          <i
                            className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                              }`}
                          ></i>{" "}
                          {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                        </button>
                      </div>
                    </div>

                    {/* ✅ Progress bar shown while Customer Export is running */}
                    {isCustomerExporting && exportProgress && (
                      <div
                        style={{
                          backgroundColor: "#fff8e1",
                          borderBottom: "1px solid #ffe082",
                          padding: "8px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "13px",
                          color: "#f57c00",
                        }}
                      >
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          style={{ color: "#f57c00" }}
                        />
                        <span>{exportProgress}</span>
                      </div>
                    )}

                    {/* Filter Section */}
                    {isFilterExpanded && (
                      <div
                        className="card-body"
                        style={{
                          borderBottom: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Period</label>
                            <select
                              className="form-select form-control-lg"
                              value={selectedPeriod}
                              onChange={(e) => handlePeriodChange(e.target.value)}
                            >
                              <option value="">Select Period</option>
                              <option value="today">Today</option>
                              <option value="yesterday">Yesterday</option>
                              <option value="thisMonth">This Month</option>
                              <option value="lastMonth">Last Month (exclude this month)</option>
                              <option value="last3Months">Last 3 Months (exclude this month)</option>
                              <option value="last6Months">Last 6 Months (exclude this month)</option>
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.fromDate}
                              onChange={(e) =>
                                setFilterParams({ ...filterParams, fromDate: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.toDate}
                              onChange={(e) =>
                                setFilterParams({ ...filterParams, toDate: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">User</label>
                            <Select
                              options={userOptions}
                              value={getSelectedOption(userOptions, filterParams.userId)}
                              onChange={(option) =>
                                setFilterParams({ ...filterParams, userId: option?.value })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select User"
                              isSearchable
                              isClearable
                            />
                          </div>
                          <div
                            className="col-12"
                            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
                          >
                            <button className="btn btn-secondary" onClick={clearFilter}>
                              Clear
                            </button>
                            <button className="btn btn-primary" onClick={applyFilter}>
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No site visited data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Site Visited Data"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        .status-count-badge:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .completed-badge {
          background: #e8f5e9;
          color: #2e7d32;
        }
        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
        .btn-outline-warning:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default SiteVisitedReport;